import { AlertTriangle, FileWarning } from 'lucide-react'
import * as React from 'react'
import { Button } from '#app/components/ui/button.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog.tsx'
import {
	formatUploadFileSize,
	UPLOAD_FILE_ERROR_EVENT,
	type UploadFileErrorEventDetail,
	type UploadFileErrorItem,
} from '#app/utils/upload-file-errors.ts'

export function UploadFileErrorDialog() {
	const [open, setOpen] = React.useState(false)
	const [errors, setErrors] = React.useState<UploadFileErrorItem[]>([])

	React.useEffect(() => {
		function handleUploadFileError(event: Event) {
			const detail = (event as CustomEvent<UploadFileErrorEventDetail>).detail
			if (!detail?.errors?.length) return

			setErrors(currentErrors => [...currentErrors, ...detail.errors])
			setOpen(true)
		}

		window.addEventListener(UPLOAD_FILE_ERROR_EVENT, handleUploadFileError)
		return () => window.removeEventListener(UPLOAD_FILE_ERROR_EVENT, handleUploadFileError)
	}, [])

	function handleOpenChange(nextOpen: boolean) {
		setOpen(nextOpen)
		if (!nextOpen) {
			setErrors([])
		}
	}

	if (errors.length === 0) return null

	const allFilesTooLarge = errors.every(error => error.fileSize > error.limitSize)
	const title = allFilesTooLarge
		? errors.length === 1
			? 'Archivo demasiado grande'
			: 'Archivos demasiado grandes'
		: errors.length === 1
			? 'No se pudo subir el archivo'
			: 'No se pudieron subir los archivos'
	const description = allFilesTooLarge
		? errors.length === 1
			? 'No se subió el archivo señalado abajo.'
			: 'No se subieron los archivos señalados abajo.'
		: 'No se completó la carga. Revisa el detalle e inténtalo nuevamente.'

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="w-[calc(100vw-2rem)] max-w-lg gap-0 overflow-hidden p-0 sm:rounded-2xl">
				<DialogHeader className="border-b border-neutral-100 px-6 py-5 text-left">
					<div className="flex items-start gap-3 pr-8">
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 ring-1 ring-red-100">
							<AlertTriangle className="h-5 w-5" aria-hidden />
						</div>
						<div className="min-w-0 pt-0.5">
							<DialogTitle className="mb-1 text-lg font-semibold leading-6 text-neutral-950">{title}</DialogTitle>
							<DialogDescription className="text-sm leading-5 text-neutral-500">{description}</DialogDescription>
						</div>
					</div>
				</DialogHeader>
				<div className="max-h-[52dvh] space-y-2.5 overflow-y-auto px-6 py-4">
					{errors.map((error, index) => {
						const fileSizeLabel = formatUploadFileSize(error.fileSize)
						const limitSizeLabel = formatUploadFileSize(error.limitSize)
						const reason =
							error.fileSize > error.limitSize
								? `El archivo que intentaste subir pesa ${fileSizeLabel}. El límite permitido es ${limitSizeLabel}. Sube un archivo de menor tamaño.`
								: error.message

						return (
							<div
								key={`${error.fileName}-${index}`}
								className="rounded-xl border border-neutral-200 bg-neutral-50/70 p-4"
							>
								<div className="flex items-start gap-3">
									<div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-red-600 shadow-sm ring-1 ring-neutral-200">
										<FileWarning className="h-4 w-4" aria-hidden />
									</div>
									<div className="min-w-0 flex-1">
										<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
											<p className="break-words text-sm font-medium leading-5 text-neutral-950">{error.fileName}</p>
											<span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-100">
												No se subió
											</span>
										</div>
										<p className="mt-1 text-sm leading-5 text-neutral-600">{reason}</p>
									</div>
								</div>
							</div>
						)
					})}
				</div>
				<DialogFooter className="border-t border-neutral-100 bg-neutral-50 px-6 py-4">
					<Button className="h-9 px-4 text-sm" onClick={() => handleOpenChange(false)}>
						Entendido
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
